'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import CakeCard from '@/components/cake/cake-card'
import { useUserStore } from '@/store/user-store'
import { Heart, Gift, ArrowRight, Loader2, Search, Star, Wallet, Crown } from 'lucide-react'
import { MEMBER_LEVELS } from '@/lib/constants'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'

interface CakeItem {
  id: number; name: string; description: string; minPrice: number; maxPrice: number; image: string
  category: string; style: string; tags: string[]; rating: number
  monthlySales: number; isHot: boolean; isNew: boolean; isSeasonal: boolean; isCustomizable: boolean; sizes: string[]
}

const BANNERS = [
  { id: 1, image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=800&h=360&fit=crop', title: '春季新品上市', subtitle: '限时满减优惠中', link: '/menu?category=生日' },
  { id: 2, image: '/images/banners/banner-birthday.png', title: '生日蛋糕专场', subtitle: '满99减20 · 满199减50', link: '/menu?category=生日' },
  { id: 3, image: 'https://images.unsplash.com/photo-1519869325930-281384150729?w=800&h=360&fit=crop', title: '定制你的专属蛋糕', subtitle: '口味/尺寸/装饰 · 一键定制', link: '/customize' },
]

export default function HomePage() {
  const { user } = useUserStore()
  const router = useRouter()
  const [cakes, setCakes] = useState<CakeItem[]>([])
  const [categories, setCategories] = useState<{name: string}[]>([])
  const [loading, setLoading] = useState(true)
  const [currentBanner, setCurrentBanner] = useState(0)
  const memberInfo = MEMBER_LEVELS.find(m => m.level === (user?.memberLevel || 1))

  useEffect(() => {
    fetch('/api/cakes?pageSize=50')
      .then(res => res.json())
      .then(data => {
        setCakes(data.items || [])
        const cats = [...new Set<string>((data.items || []).map((c: any) => c.category))]
        setCategories(cats.map(name => ({ name })))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const timer = setInterval(() => setCurrentBanner(prev => (prev + 1) % BANNERS.length), 4000)
    return () => clearInterval(timer)
  }, [])

  const hotCakes = cakes.filter(c => c.isHot).slice(0, 6)
  const seasonalCakes = cakes.filter(c => c.isSeasonal).slice(0, 4)
  const customizableCakes = cakes.filter(c => c.isCustomizable).slice(0, 4)

  return (
    <div className="min-h-screen pb-16">
      <TopBar />

      {user && (
        <Link href="/profile" className="mx-4 mt-3 p-3 bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-100 rounded-2xl flex items-center gap-3 active:bg-pink-100 transition-colors">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {user.nickname.slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-gray-800 truncate">{user.nickname}</span>
              <span className="px-1.5 py-0.5 text-[9px] font-bold rounded" style={{ backgroundColor: `${memberInfo?.color}20`, color: memberInfo?.color }}>
                <Crown className="w-2.5 h-2.5 inline mr-0.5" />{memberInfo?.name}
              </span>
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5 font-mono">ID: {user.id}</p>
          </div>
          <div className="flex items-center gap-3 text-center flex-shrink-0">
            <div>
              <div className="flex items-center gap-0.5 justify-center">
                <Star className="w-3 h-3 text-amber-500" />
                <span className="text-sm font-bold text-gray-700">{user.points}</span>
              </div>
              <p className="text-[9px] text-gray-400">积分</p>
            </div>
            <div>
              <div className="flex items-center gap-0.5 justify-center">
                <Wallet className="w-3 h-3 text-green-500" />
                <span className="text-sm font-bold text-gray-700">¥{user.balance.toFixed(0)}</span>
              </div>
              <p className="text-[9px] text-gray-400">余额</p>
            </div>
          </div>
        </Link>
      )}

      {/* 搜索栏 */}
      <div className="mx-4 mt-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-xl cursor-pointer active:bg-gray-200 transition-colors"
          onClick={() => router.push('/search')}>
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-400">搜索蛋糕...</span>
        </div>
      </div>

      {/* Banner */}
      <div className="relative mx-4 mt-3 rounded-xl overflow-hidden h-40">
        {BANNERS.map((banner, idx) => (
          <Link key={banner.id} href={banner.link}
            className={`absolute inset-0 transition-opacity duration-500 ${idx === currentBanner ? 'opacity-100' : 'opacity-0'}`}
          >
            <Image src={banner.image} alt={banner.title} fill className="object-cover" unoptimized />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-4 text-white">
              <p className="text-base font-bold">{banner.title}</p>
              <p className="text-xs opacity-90">{banner.subtitle}</p>
            </div>
          </Link>
        ))}
        <div className="absolute bottom-2 right-3 flex gap-1">
          {BANNERS.map((_, idx) => (
            <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentBanner ? 'bg-white w-4' : 'bg-white/50'}`} />
          ))}
        </div>
      </div>

      {/* 分类 */}
      <div className="flex gap-4 overflow-x-auto px-4 mt-4 scrollbar-hide">
        {categories.length > 0 ? categories.map(cat => (
          <Link key={cat.name} href={`/menu?category=${cat.name}`} className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center"><span className="text-lg">🎂</span></div>
            <span className="text-[11px] text-gray-600 font-medium">{cat.name}</span>
          </Link>
        )) : (
          ['生日', '情人节', '儿童', '企业', '下午茶'].map(name => (
            <div key={name} className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-gray-100 animate-pulse" />
              <div className="w-10 h-3 bg-gray-100 rounded animate-pulse" />
            </div>
          ))
        )}
      </div>

      {/* 新人专享 - 点击有反馈 */}
      <div className="mx-4 mt-4 p-3 bg-gradient-to-r from-pink-500 to-rose-400 rounded-xl text-white active:scale-[0.98] transition-transform cursor-pointer" onClick={() => toast.success('新人礼包已自动发放，请查看「我的」优惠券')}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            <div>
              <p className="text-sm font-bold">新人专享礼包</p>
              <p className="text-[10px] opacity-90">注册即领满99减20优惠券 + 100积分</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center pt-16"><Loader2 className="w-6 h-6 text-pink-500 animate-spin" /></div>
      ) : (
        <>
          {hotCakes.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between px-4 mb-3">
                <h2 className="text-base font-bold text-gray-800">🔥 热门推荐</h2>
                <Link href="/menu?sort=sales" className="text-xs text-pink-500 flex items-center gap-0.5">查看全部 <ArrowRight className="w-3 h-3" /></Link>
              </div>
              <div className="flex gap-3 overflow-x-auto scrollbar-hide px-4">
                {hotCakes.map(cake => (
                  <Link key={cake.id} href={`/cake/${cake.id}`} className="flex-shrink-0 w-36">
                    <div className="relative w-36 h-36 rounded-xl overflow-hidden bg-gray-100">
                      <Image src={cake.image} alt={cake.name} fill className="object-cover" unoptimized />
                      {cake.isHot && <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">爆款</span>}
                    </div>
                    <p className="mt-1.5 text-sm font-medium text-gray-800 truncate">{cake.name}</p>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-sm font-bold text-pink-500">¥{cake.minPrice}{cake.maxPrice > cake.minPrice ? '~' + cake.maxPrice : ''}</p>
                      <p className="text-[10px] text-gray-400">{cake.monthlySales}月售</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {seasonalCakes.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between px-4 mb-3">
                <h2 className="text-base font-bold text-gray-800">🌸 季节限定</h2>
                <Link href="/menu" className="text-xs text-pink-500 flex items-center gap-0.5">查看全部 <ArrowRight className="w-3 h-3" /></Link>
              </div>
              <div className="grid grid-cols-2 gap-3 px-4">{seasonalCakes.map(cake => <CakeCard key={cake.id} cake={cake} />)}</div>
            </div>
          )}

          {customizableCakes.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between px-4 mb-3">
                <h2 className="text-base font-bold text-gray-800">🎨 支持定制</h2>
                <Link href="/customize" className="text-xs text-pink-500 flex items-center gap-0.5">去定制 <ArrowRight className="w-3 h-3" /></Link>
              </div>
              <div className="grid grid-cols-2 gap-3 px-4">{customizableCakes.map(cake => <CakeCard key={cake.id} cake={cake} />)}</div>
            </div>
          )}

          <div className="mt-5">
            <div className="flex items-center justify-between px-4 mb-3">
              <h2 className="text-base font-bold text-gray-800">🍰 全部商品</h2>
              <Link href="/menu" className="text-xs text-pink-500 flex items-center gap-0.5">查看全部 <ArrowRight className="w-3 h-3" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-3 px-4">{cakes.slice(0, 6).map(cake => <CakeCard key={cake.id} cake={cake} />)}</div>
          </div>
        </>
      )}

      {/* 私域福利 */}
      <div className="mx-4 mt-5 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-bold text-amber-700">加入私域，更多福利</span>
        </div>
        <p className="text-xs text-amber-600 mb-3">关注公众号「甜蜜时光」获取专属优惠、新品试吃、生日惊喜</p>
        <button onClick={() => toast.info('请搜索微信公众号「甜蜜时光」关注，获取专属福利')} className="w-full py-2 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600 transition-colors active:scale-[0.98]">
          立即关注
        </button>
      </div>

      <div className="h-4" />
      <BottomNav />
    </div>
  )
}
